// AllTerra Central — PDP media-gallery accessibility patches
//
// Corrects two WCAG 2.2 AA failures that originate in Horizon's base
// media-gallery/slideshow component, scoped to the product media gallery so
// no Horizon core files are touched:
//
//   1. aria-allowed-attr (WCAG 4.1.2, Level A) — the slideshow thumbnail (and
//      dot) buttons carry `aria-selected`, which is not a permitted attribute
//      on an implicit role="button". We mirror it to `aria-pressed` (a valid
//      toggle-button state) and strip `aria-selected`. Horizon's slideshow.js
//      re-sets `aria-selected` on every slide change (slideshow.js:428), so a
//      MutationObserver keeps `aria-pressed` in sync afterwards.
//
//   2. scrollable-region-focusable (WCAG 2.1.1, Level A) — the
//      <slideshow-slides> scroll container has tabindex="-1", making the
//      scrollable region keyboard-inoperable. We make it focusable and label
//      it so keyboard users can pan the gallery.
//
// The matching visual for the active thumbnail (which Horizon styles via
// [aria-selected='true']) is reproduced against [aria-pressed='true'] in
// assets/at-pdp.css.

(() => {
  const galleries = document.querySelectorAll('media-gallery');
  if (!galleries.length) return;

  const syncPressed = (btn) => {
    const selected = btn.getAttribute('aria-selected');
    if (selected === null) return;
    btn.setAttribute('aria-pressed', selected);
    btn.removeAttribute('aria-selected');
  };

  galleries.forEach((gallery) => {
    // Fix #1 — thumbnails + dots: aria-selected -> aria-pressed, kept in sync.
    const controls = gallery.querySelectorAll(
      '.slideshow-controls__thumbnail, .slideshow-controls__dots button'
    );
    if (controls.length) {
      controls.forEach(syncPressed);

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName === 'aria-selected') {
            syncPressed(mutation.target);
          }
        }
      });
      controls.forEach((btn) =>
        observer.observe(btn, {
          attributes: true,
          attributeFilter: ['aria-selected'],
        })
      );
    }

    // Fix #2 — make the gallery scroll region keyboard-focusable. It needs a
    // role for the aria-label to be valid (aria-prohibited-attr): role="group"
    // makes the labelled, focusable scroll region a meaningful stop without
    // adding a page landmark the way role="region" would.
    gallery.querySelectorAll('slideshow-slides[tabindex="-1"]').forEach((scroller) => {
      scroller.setAttribute('tabindex', '0');
      if (!scroller.hasAttribute('role')) {
        scroller.setAttribute('role', 'group');
      }
      if (!scroller.hasAttribute('aria-label')) {
        scroller.setAttribute('aria-label', 'Product media gallery');
      }
    });
  });
})();
